import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../lib/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ShieldAlert, Plus, Trash2, Edit2, X, UserPlus, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

export default function AdminUsers() {
    const { user: currentUser } = useAuth();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ name: '', email: '', password: '' });
    const [creating, setCreating] = useState(false);
    const [showPw, setShowPw] = useState(false);

    const [editingAdmin, setEditingAdmin] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', email: '' });
    const [saving, setSaving] = useState(false);

    const loadAdmins = () => {
        setLoading(true);
        adminApi.getAdmins().then(({ data }) => setAdmins(data)).catch(() => toast.error('Failed to load admins')).finally(() => setLoading(false));
    };

    useEffect(() => { loadAdmins(); }, []);

    const handleCreate = async () => {
        if (!form.name || !form.email || !form.password) { toast.error('All fields are required'); return; }
        setCreating(true);
        try {
            await adminApi.createAdmin(form);
            toast.success('Admin created');
            setShowForm(false);
            setForm({ name: '', email: '', password: '' });
            loadAdmins();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create admin'); }
        finally { setCreating(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this administrator?')) return;
        try { await adminApi.deleteAdmin(id); toast.success('Admin deleted'); loadAdmins(); }
        catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
    };

    const startEdit = (a) => { setEditingAdmin(a); setEditForm({ name: a.name, email: a.email }); };

    const handleSaveEdit = async () => {
        if (!editForm.name.trim() || !editForm.email.trim()) { toast.error('Name and email are required'); return; }
        setSaving(true);
        try {
            const updates = {};
            if (editForm.name !== editingAdmin.name) updates.name = editForm.name;
            if (editForm.email !== editingAdmin.email) updates.email = editForm.email;
            if (Object.keys(updates).length === 0) { setEditingAdmin(null); return; }
            await adminApi.updateAdmin(editingAdmin.id, updates);
            toast.success('Admin updated');
            setEditingAdmin(null);
            loadAdmins();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
        finally { setSaving(false); }
    };

    return (
        <Layout>
            <div data-testid="admin-users-page">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
                            <ShieldAlert className="h-6 w-6 brand-icon" /> Administrators
                        </h1>
                        <p className="text-slate-400 text-sm mt-1">{admins.length} platform admin{admins.length !== 1 ? 's' : ''}</p>
                    </div>
                    <Button onClick={() => setShowForm(!showForm)} className="brand-btn font-medium" data-testid="add-admin-button">
                        <UserPlus className="h-4 w-4 mr-1" /> Add Admin
                    </Button>
                </div>

                {showForm && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 mb-6" data-testid="add-admin-form">
                        <h2 className="text-lg font-semibold text-slate-200 mb-4">New Administrator</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div>
                                <Label className="text-slate-300">Full Name *</Label>
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="Full name" data-testid="new-admin-name" />
                            </div>
                            <div>
                                <Label className="text-slate-300">Email *</Label>
                                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="admin@foodsafehub.co.uk" data-testid="new-admin-email" />
                            </div>
                            <div>
                                <Label className="text-slate-300">Password *</Label>
                                <div className="relative mt-1">
                                    <Input type={showPw ? 'text' : 'password'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                                        className="bg-slate-800 border-slate-700 text-slate-100 pr-9" placeholder="Secure password" data-testid="new-admin-password" />
                                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                                        {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                            <Button onClick={handleCreate} disabled={creating} className="brand-btn font-medium" data-testid="save-admin-button">
                                {creating ? 'Creating...' : 'Create Admin'}
                            </Button>
                            <Button variant="outline" onClick={() => setShowForm(false)} className="border-slate-700 text-slate-300">Cancel</Button>
                        </div>
                    </div>
                )}

                {editingAdmin && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
                        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setEditingAdmin(null)} />
                        <div className="relative bg-slate-900 border border-slate-800 rounded-xl p-5 w-full max-w-md" data-testid="edit-admin-modal">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-slate-200">Edit Administrator</h2>
                                <Button variant="ghost" size="icon" onClick={() => setEditingAdmin(null)} className="text-slate-400 h-7 w-7"><X className="h-4 w-4" /></Button>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <Label className="text-slate-300">Name</Label>
                                    <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                        className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="edit-admin-name" />
                                </div>
                                <div>
                                    <Label className="text-slate-300">Email</Label>
                                    <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                        className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="edit-admin-email" />
                                </div>
                            </div>
                            <div className="flex gap-2 mt-5">
                                <Button onClick={handleSaveEdit} disabled={saving} className="brand-btn font-medium" data-testid="save-edit-admin-button">
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </Button>
                                <Button variant="outline" onClick={() => setEditingAdmin(null)} className="border-slate-700 text-slate-300">Cancel</Button>
                            </div>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="space-y-3">{[1, 2].map(i => <div key={i} className="h-16 bg-slate-900/60 rounded-xl animate-pulse" />)}</div>
                ) : admins.length === 0 ? (
                    <div className="text-center py-16 text-slate-500"><ShieldAlert className="h-12 w-12 mx-auto mb-3 opacity-30" /><p>No administrators</p></div>
                ) : (
                    <div className="space-y-2">
                        {admins.map((a) => {
                            const isSelf = a.id === currentUser?.id;
                            return (
                                <div key={a.id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-4" data-testid={`admin-card-${a.id}`}>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0 bg-rose-500/10 text-rose-400">
                                                <ShieldAlert className="h-5 w-5" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-slate-200 truncate">{a.name}{isSelf && ' (You)'}</p>
                                                <p className="text-xs text-slate-500 truncate">{a.email}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <span className="text-[11px] text-slate-600">{formatDate(a.created_at)}</span>
                                            {!isSelf && (
                                                <>
                                                    <Button size="sm" variant="ghost" className="text-slate-400 hover:text-slate-200 hover:bg-slate-700 h-7 w-7 p-0"
                                                        onClick={() => startEdit(a)} data-testid={`edit-admin-${a.id}`} title="Edit admin">
                                                        <Edit2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                    <Button size="sm" variant="ghost" className="text-rose-400 hover:bg-rose-500/10 h-7 w-7 p-0"
                                                        onClick={() => handleDelete(a.id)} data-testid={`delete-admin-${a.id}`} title="Delete admin">
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </Layout>
    );
}
